import Vue from 'vue';
import { BfButton } from 'components/Butterfly';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import DropdownItem from 'components/Butterfly/Dropdown/DropdownItem.vue';
import Pikaday from 'pikaday';
import RadioButtons from "components/widgets/RadioButtons.vue";
import { DateRangeTypeEnum } from "types/DashboardTypes";
import { calcRangeDates, dateRangeHints, formatDate, strToDate } from 'src/utils/dates';
const DateRangePicker = Vue.extend({
    components: { Dropdown, DropdownItem, BfButton, RadioButtons },
    props: {
        type: { type: String, required: false, default: null },
        dateFrom: { type: String, required: false, default: '' },
        dateTo: { type: String, required: false, default: '' },
        dateField: { type: String, required: false, default: '' },
        dateFields: { type: Array, required: true },
        defaultDateField: { type: String, required: false, default: '' },
    },
    data() {
        return {
            stagedType: null,
            stagedDateField: null,
            stagedDateEnd: null,
            stagedDateStart: null,
            pika: null,
        };
    },
    computed: {
        defaultDates() {
            const dates = calcRangeDates();
            switch (this.stagedType || this.type) {
                case (null):
                case DateRangeTypeEnum.CUSTOM: {
                    return [this.stagedDateStart || this.dateFrom, this.stagedDateEnd || this.dateTo];
                }
                default: {
                    return [dates[this.stagedType || this.type]?.from, dates[this.stagedType || this.type]?.to];
                }
            }
        },
        allowApply() {
            const relevantFieldChange = ((this.stagedType !== DateRangeTypeEnum.ALL_TIME && this.stagedType !== null) ||
                this.type !== DateRangeTypeEnum.ALL_TIME) &&
                (this.stagedDateField !== null && this.dateField !== this.stagedDateField);
            const toFromChange = (this.stagedDateStart !== null && (this.dateFrom !== this.stagedDateStart)) ||
                (this.stagedDateEnd !== null && (this.dateTo !== this.stagedDateEnd));
            const typeChange = this.stagedType !== null && (this.type !== this.stagedType);
            return relevantFieldChange || toFromChange || typeChange;
        },
        rangeTypes() {
            const types = Object.values(DateRangeTypeEnum).map(t => ({ label: t, value: t }));
            return [
                types.slice(0, 2),
                types.slice(2)
            ];
        },
        fromLabel() {
            // ------------------------------------------------------------------------------------------------
            if ((this.stagedType || this.type) === DateRangeTypeEnum.ALL_TIME) {
                return [DateRangeTypeEnum.ALL_TIME, ''];
            }
            if ((this.stagedType || this.type) === DateRangeTypeEnum.CUSTOM) {
                return [this.stagedDateStart || this.dateFrom || 'All time', ''];
            }
            return [calcRangeDates()[this.stagedType || this.type]?.from, `(${dateRangeHints[this.stagedType || this.type]?.from})`];
        },
        toLabel() {
            // ------------------------------------------------------------------------------------------------
            if ((this.stagedType || this.type) === DateRangeTypeEnum.ALL_TIME) {
                return [DateRangeTypeEnum.ALL_TIME, ''];
            }
            if ((this.stagedType || this.type) === DateRangeTypeEnum.CUSTOM) {
                return [this.stagedDateEnd || this.dateTo || 'All time', ''];
            }
            return [calcRangeDates()[this.stagedType || this.type]?.to, `(${dateRangeHints[this.stagedType || this.type]?.to})`];
        },
    },
    methods: {
        closePika() {
            if (this.pika)
                this.pika.hide();
        },
        showCalendar(calendar) {
            let field = '';
            let defaultDate = new Date();
            let onSelect = (date) => { date; };
            if (calendar === 'start') {
                field = 'startDate';
                defaultDate = strToDate(this.defaultDates[0]);
                onSelect = (date) => this.setStagedStartDate(date);
            }
            if (calendar === 'end') {
                field = 'endDate';
                defaultDate = strToDate(this.defaultDates[1]);
                onSelect = (date) => this.setStagedEndDate(date);
            }
            if (this.pika) {
                this.pika.destroy();
            }
            this.pika = new Pikaday({
                field: this.$refs[field],
                container: this.$refs.datePickerMenu,
                reposition: false,
                defaultDate,
                setDefaultDate: true,
                onSelect,
                onOpen: () => {
                    this.pika.el.style.left = this.$refs[field].offsetLeft + 'px';
                    this.pika.el.style.top = (this.$refs[field].offsetTop + this.$refs[field].offsetHeight + 'px');
                },
                onClose: () => {
                    this.pika.destroy();
                }
            });
            this.pika.show();
        },
        setStagedStartDate(date) {
            /**
             if we are setting a date here then it's for CUSTOM range so
             set the range type to CUSTOM
            */
            const dateString = formatDate(date);
            this.setRangeType(DateRangeTypeEnum.CUSTOM);
            this.stagedDateStart = dateString;
        },
        setStagedEndDate(date) {
            /**
             if we are setting a date here then it's for CUSTOM range so
             set the range type to CUSTOM
            */
            const dateString = formatDate(date);
            this.setRangeType(DateRangeTypeEnum.CUSTOM);
            this.stagedDateEnd = dateString;
        },
        setRangeType(type) {
            this.closePika();
            // ------------------------------------------------------------------------------------------------
            /**
             When setting the range type we may need to set or clear
             the to & from dates as well if the type is changing depending
             on what it was set to previously
             */
            const prevType = this.stagedType || this.type;
            this.stagedType = type;
            if (prevType === type)
                return;
            if (type === DateRangeTypeEnum.CUSTOM) {
                if (prevType === DateRangeTypeEnum.ALL_TIME) {
                    this.stagedDateEnd = '';
                    this.stagedDateStart = '';
                    return;
                }
                this.stagedDateEnd = calcRangeDates()[prevType]?.to;
                this.stagedDateStart = calcRangeDates()[prevType]?.from;
                return;
            }
            this.stagedDateEnd = '';
            this.stagedDateStart = '';
        },
        cancel() {
            this.$emit('cancel');
            this.stagedType = null;
            this.stagedDateField = null;
            this.stagedDateEnd = null;
            this.stagedDateStart = null;
        },
        apply() {
            this.$emit('apply', this.stagedDateField || this.dateField || this.defaultDateField, this.stagedDateStart ?? this.dateFrom, this.stagedDateEnd ?? this.dateTo, this.stagedType || this.type);
            this.stagedType = null;
            this.stagedDateField = null;
            this.stagedDateEnd = null;
            this.stagedDateStart = null;
        },
        clearDateFilters() {
            this.setRangeType(this.type ?? DateRangeTypeEnum.CUSTOM);
            this.stagedDateEnd = this.dateTo ?? null;
            this.stagedDateStart = this.dateFrom ?? null;
            this.stagedDateField = this.dateField ?? this.defaultDateField ?? null;
        },
    }
});
export default DateRangePicker;
